// src/hooks/useTeamHistory.js
import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

export default function useTeamHistory(currentTeamScores = null) {
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);

            // Kreiraj array poslednih 10 dana (uključujući danas)
            const dates = [];
            const today = new Date();

            for (let i = 0; i < 10; i++) {
                const date = new Date(today);
                date.setDate(today.getDate() - i);
                dates.push(date.toDateString());
            }

            // Dohvati podatke za svaki dan (osim danas ako imamo currentTeamScores)
            const historyPromises = dates.map(async (dateString, index) => {
                try {
                    // Ako je današnji dan i imamo currentTeamScores, koristi ih
                    if (index === 0 && currentTeamScores) {
                        // Prikaži danas samo ako ima rezultata
                        if (currentTeamScores.blackWhite > 0 || currentTeamScores.redWhite > 0) {
                            return {
                                date: dateString,
                                blackWhite: currentTeamScores.blackWhite || 0,
                                redWhite: currentTeamScores.redWhite || 0,
                                isToday: true
                            };
                        } else {
                            return null; // Ignoriši ako nema rezultata
                        }
                    }

                    const dailyScoreRef = doc(db, 'dailyScores', dateString);
                    const docSnap = await getDoc(dailyScoreRef);

                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        // Prikaži samo ako ima rezultata
                        if (data.blackWhite > 0 || data.redWhite > 0) {
                            return {
                                date: dateString,
                                blackWhite: data.blackWhite || 0,
                                redWhite: data.redWhite || 0,
                                isToday: index === 0
                            };
                        } else {
                            return null; // Ignoriši ako nema rezultata
                        }
                    } else {
                        return null; // Ignoriši ako dokument ne postoji
                    }
                } catch (err) {
                    console.error(`Error fetching data for ${dateString}:`, err);
                    return null; // Ignoriši u slučaju greške
                }
            });

            const historyResults = await Promise.all(historyPromises);
            // Filtriraj null rezultate (dane bez rezultata)
            const history = historyResults.filter(day => day !== null);

            setHistoryData(history);
            setError(null);
        } catch (err) {
            console.error("Error fetching team history:", err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []); // Uklonili currentTeamScores iz dependency array

    // Optimistic update za današnji dan
    useEffect(() => {
        if (currentTeamScores && historyData.length > 0) {
            const today = new Date().toDateString();

            setHistoryData(prevHistory => {
                const updatedHistory = [...prevHistory];
                const todayIndex = updatedHistory.findIndex(day => day.isToday);

                if (todayIndex !== -1) {
                    // Ažuriraj postojeći današnji dan
                    updatedHistory[todayIndex] = {
                        ...updatedHistory[todayIndex],
                        blackWhite: currentTeamScores.blackWhite || 0,
                        redWhite: currentTeamScores.redWhite || 0
                    };
                } else if (currentTeamScores.blackWhite > 0 || currentTeamScores.redWhite > 0) {
                    // Dodaj današnji dan na početak ako ima rezultata i ne postoji
                    updatedHistory.unshift({
                        date: today,
                        blackWhite: currentTeamScores.blackWhite || 0,
                        redWhite: currentTeamScores.redWhite || 0,
                        isToday: true
                    });
                }

                return updatedHistory;
            });
        }
    }, [currentTeamScores, historyData.length]);

    // Initial fetch samo jednom
    useEffect(() => {
        fetchHistory().catch(err => {
            console.error('Error in fetchHistory:', err);
            setError(err);
            setLoading(false);
        });
    }, []); // Prazna dependency array

    const refetch = () => {
        return fetchHistory();
    };

    return { historyData, loading, error, refetch };
}